import { SelectField, TypeaheadField } from '@flos/react-ui';
import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { useRecoilState, useRecoilValue } from 'recoil';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import type { CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { useSaveCoverableInformation } from '../../../../hooks/useSaveCoverableInformation';
import { petBreedsAtom } from '../../../../recoil/atoms/petBreedsAtom';
import { useState } from 'react';
import {
    getCoverableFromAllOnsaCoverables,
    getCoverableIndex
} from '../../../../utils/coverablesUtil';
import { dogUseListAtom } from '../../../../recoil/atoms/dogUseListAtom';
import ViewInformationModal from '../../../../shared_components/ViewInformationModal/ViewInformationModal';
import RemoveModal from '../../../../shared_components/RemoveModal/RemoveModal';
import { useHippoText } from '../../../../hooks/useHippoText';
import { track } from '../../../../utils/trackingUtil';
import HelpLink from '../../../../shared_components/HelpLink/HelpLink';

const DogBreedStep = ({
    goNext,
    goPrevious
}: {
    goNext: () => void;
    goPrevious: () => void;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const [coverables, setCoverables] = useRecoilState(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const dogBreeds = useRecoilValue(petBreedsAtom);
    const dogBreedsNames = dogBreeds
        .map((breed) => breed.name)
        .sort((breed1, breed2) => breed1.localeCompare(breed2, 'en'));
    const dogUseList = useRecoilValue(dogUseListAtom);
    const dogUseListNames = dogUseList.map((dogUse) => dogUse.name);
    const [isValid, setIsValid] = useState<boolean>(false);

    const { dogs = [] } = coverables;
    const coverableIndex = getCoverableIndex(dogs, currentProduct.fixedId);
    const coverable = dogs[coverableIndex];
    const { dogUseId = 'Family' } = coverable;

    const breed = dogBreeds.find((breed) => breed.publicId === coverable.breedId);
    const dogUse = dogUseList.find((dogUse) => dogUse.code === dogUseId)?.name;

    const [isExpand, setIsExpand] = useState<boolean>(!!(breed && dogUse));
    const [showModal, setShowModal] = useState<boolean>(false);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const currentCoverable = getCoverableFromAllOnsaCoverables(
        coverables,
        currentProduct.fixedId,
        currentProduct.lobCode
    );

    const handleDogBreedChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;
        const foundBreed = dogBreeds.find((breed) => breed.name === value);

        if (foundBreed?.isIllegal) {
            setShowRemoveModal(true);
        }

        const updatedCoverables = [...dogs];

        updatedCoverables[coverableIndex] = {
            ...updatedCoverables[coverableIndex],
            breedId: foundBreed?.publicId
        };

        setCoverables((prev) => ({
            ...prev,
            dogs: updatedCoverables
        }));
    };

    const handleDogUseChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const { value } = event.target;
        const foundDogUse = dogUseList.find((dogUse) => dogUse.name === value);

        const updatedCoverables = [...dogs];

        updatedCoverables[coverableIndex] = {
            ...updatedCoverables[coverableIndex],
            dogUseId: foundDogUse?.code
        };

        setCoverables((prev) => ({
            ...prev,
            dogs: updatedCoverables
        }));
    };

    const onNext = (): void => {
        setIsLoading(true);
        const updatedCoverable: CoverableInformationToUpdateDTO = {
            dog: {
                publicId: coverable.publicId,
                fixedId: coverable.fixedId,
                breedId: coverable.breedId,
                ...(isExpand ? { dogUseId } : {})
            },
            productToUpdate: currentProduct.flavourName
        };

        void saveCoverableInformation(updatedCoverable).then(() => {
            isExpand ? goNext() : setIsExpand(true);

            if (isExpand) {
                track(`${coverable.flavourName}CoverableInformationBreedOfDogAndDogFunction`, [
                    { breed: breed?.name },
                    { dogFunction: dogUseId }
                ]);
            }

            setIsLoading(false);
        });
    };

    return (
        <WizardPagetemplate
            onNext={onNext}
            onPrevious={goPrevious}
            disableNext={!isValid || isLoading || breed?.isIllegal}
            isNextLoading={isLoading}
            disablePrevious={isLoading}
        >
            <CoverableDetailsTemplate
                header={getHippoText('DSS-PET-COVERABLE-INFORMATION-PET-BREED-QUESTION', {
                    petname: coverable.name ?? ''
                })}
            >
                <TypeaheadField
                    label={getHippoText(
                        'DSS-PET-COVERABLE-INFORMATION-PET-BREED-QUESTION-INPUT-TOOLTIP'
                    )}
                    value={breed?.name}
                    onValidityChange={(isValid) => {
                        setIsValid(!!isValid);
                    }}
                    onChange={handleDogBreedChange}
                    options={dogBreedsNames}
                    required
                    disabled={isLoading}
                />
                <>
                    {isExpand && (
                        <>
                            <h4>
                                {getHippoText('DSS-PET-COVERABLE-INFORMATION-FAMILY-DOG-QUESTION')}
                            </h4>
                            <SelectField
                                label={getHippoText(
                                    'DSS-PET-COVERABLE-INFORMATION-FAMILY-DOG-INPUT-TOOLTIP'
                                )}
                                value={dogUse}
                                isValid={!!dogUse}
                                onValidityChange={(isValid) => {
                                    setIsValid(!!isValid);
                                }}
                                onChange={handleDogUseChange}
                                options={dogUseListNames}
                                required
                                disabled={isLoading}
                            />
                            <HelpLink
                                onClick={() => {
                                    setShowModal(true);
                                }}
                            >
                                {getHippoText('DSS-PET-COVERABLE-INFORMATION-HELP-ME-CHOOSE')}
                            </HelpLink>
                        </>
                    )}
                </>
            </CoverableDetailsTemplate>
            <ViewInformationModal
                closeModal={() => {
                    setShowModal(false);
                }}
                modalProps={{
                    title: getHippoText(
                        'DSS-PET-COVERABLE-INFORMATION-FAMILY-DOG-EXPLANATION-TITLE'
                    ),
                    description: getHippoText(
                        'DSS-PET-COVERABLE-INFORMATION-FAMILY-DOG-EXPLANATION'
                    )
                }}
                showModal={showModal}
            />
            {showRemoveModal && currentCoverable && (
                <RemoveModal
                    coverable={currentCoverable}
                    title={getHippoText('DSS-REMOVE-CONTINUE-MODAL-ILLEGAL-DOG-HEADER', {
                        dog_name: currentCoverable?.name ?? ''
                    })}
                    description={getHippoText('DSS-REMOVE-CONTINUE-MODAL-ILLEGAL-DOG-SUBHEADER')}
                    isRemoveAndContinueModal
                    showModal={showRemoveModal}
                    closeModal={() => {
                        setShowRemoveModal(false);
                    }}
                />
            )}
        </WizardPagetemplate>
    );
};

export default DogBreedStep;
